import { useEffect, useRef, useState } from "react";
import { HmsCall } from "@/components/HmsCall";
import { PhoneOff, Wifi, WifiOff, AlertTriangle, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtTime } from "@/lib/api";

export function VideoRoom({ apptId, onChange }) {
  const [state, setState] = useState("idle");
  const [room, setRoom] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const call = useRef(null);
  const ev = (type, note = "") => api.post(`/consultations/${apptId}/events`, { type, note }).catch(() => {});

  useEffect(() => {
    const on = () => { setOnline(true); ev("reconnected"); };
    const off = () => { setOnline(false); ev("connection_lost"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); call.current?.leave().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async () => {
    setState("joining");
    try {
      const { data } = await api.post(`/consultations/${apptId}/join`);
      try { await navigator.mediaDevices?.getUserMedia({ audio: true, video: data.mode === "video" }).then((s) => s.getTracks().forEach((t) => t.stop())); }
      catch { ev("media_permission_denied"); toast.warning("Camera/microphone permission was blocked. Allow access in your browser settings."); }
      setRoom(data);
      setState("connecting");
      onChange?.();
    } catch (e) {
      setState("error");
      toast.error(errMsg(e));
    }
  };
  const joined = () => { setState("live"); ev("joined"); };
  const failed = (e) => { setRoom(null); setState("error"); ev("connection_lost", String(e?.message || e?.description || "SDK error").slice(0, 150)); toast.error("Video connection failed. Tap Rejoin, or use the text chat below."); };
  const left = () => { call.current?.leave().catch(() => {}); setRoom(null); setState("ended"); ev("left"); onChange?.(); };
  const techFail = async () => { await ev("technical_failure", "Reported from room"); toast.success("Issue recorded. The consultation can be rescheduled at no cost."); left(); };

  return (
    <div className="surface overflow-hidden" data-testid="video-room">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8ED]">
        <div className="flex items-center gap-2 text-sm">
          <span className={`h-2 w-2 rounded-full ${state === "live" ? "bg-[#086B2D]" : state === "error" ? "bg-red-500" : "bg-[#FF9700]"}`} />
          <span data-testid="video-connection-status" className="capitalize">{state === "idle" ? "Not joined" : state}</span>
          <span className="muted flex items-center gap-1 ml-2">{online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5 text-red-500" />}{online ? "Online" : "Offline"}</span>
        </div>
        <span className="text-[11px] muted">{room?.mode === "audio" ? "Audio-only · " : ""}Private room · Not recorded</span>
      </div>
      <div className="relative bg-[#0E1A24] h-[70vh] min-h-[420px] max-h-[720px]" data-testid="video-stage">
        {room ? <div className="absolute inset-0"><HmsCall data={room} onJoin={joined} onLeave={left} onError={failed} callRef={call} /></div> : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white p-6 text-center">
            {state === "joining" ? <Loader2 className="h-6 w-6 animate-spin" /> : (
              <>
                <p className="text-sm text-white/70">{state === "ended" ? "You left the consultation." : state === "error" ? "Could not join right now." : "Your camera and microphone will be requested when you join."}</p>
                <Button onClick={join} className="rounded-full" data-testid="video-join-btn">{state === "idle" ? "Join consultation" : <><RefreshCw className="h-4 w-4 mr-2" />Rejoin</>}</Button>
              </>
            )}
          </div>
        )}
      </div>
      {room && (
        <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-3 border-t border-[#E2E8ED]">
          <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700" onClick={left} data-testid="video-end-call"><PhoneOff className="h-4 w-4 mr-1.5" />Leave</Button>
          <button onClick={techFail} className="text-xs text-[#9A5B00] flex items-center gap-1" data-testid="video-report-issue"><AlertTriangle className="h-3.5 w-3.5" />Report technical issue</button>
        </div>
      )}
    </div>
  );
}

export function ChatPanel({ apptId, readOnly }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/consultations/${apptId}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [apptId]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/consultations/${apptId}/messages`, { text }); setText(""); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="surface flex flex-col h-[360px]" data-testid="chat-panel">
      <div className="px-4 py-3 border-b border-[#E2E8ED] text-sm font-semibold">Secure text chat <span className="font-normal muted text-xs">· fallback if video drops</span></div>
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {msgs.length === 0 && <p className="text-sm muted">No messages yet.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[85%] ${m.sender_role === "doctor" ? "" : "ml-auto text-right"}`}>
            <div className={`inline-block rounded-2xl px-3 py-2 text-sm ${m.sender_role === "doctor" ? "bg-[#086477]/10" : "bg-[#086B2D] text-white"}`}>{m.text}</div>
            <p className="text-[10px] muted mt-1">{m.sender_name} · {fmtTime(m.at)}</p>
          </div>
        ))}
        <div ref={end} />
      </div>
      {!readOnly && (
        <form onSubmit={send} className="p-3 border-t border-[#E2E8ED] flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message" maxLength={1000} data-testid="chat-input" />
          <Button size="icon" type="submit" data-testid="chat-send-btn" aria-label="Send"><Send className="h-4 w-4" /></Button>
        </form>
      )}
    </div>
  );
}
